import { Badge, Empty, Menu, Popover, Skeleton } from 'antd';
import type { MenuProps } from 'antd';
import clsx from 'clsx';
import { useMemo } from 'react';
import { RiArrowDownSLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { useWorkSpaceSwitch } from '../hooks/use-workspace-switch';
import { useNavigate } from 'react-router-dom';
import { useActiveWorkspaces } from '../hooks/use-activeWorkspace';
import { CreateWorkspace } from './create-workspace';
import { WorkspaceItem } from './workspace-item';
import { SearchWorkspace } from './search-workspace';
import { getFormattedWalletAmount } from '@modules/settings/subscription/utils/utils';
import { AppAvatar } from '@components/app-avatar';
import { capitalize } from '@shared/utils/capitalize';
import { useTranslation } from 'react-i18next';
import { useWalletVisibilityStore } from '@modules/settings/wallet/store/wallet-visibility.store';

export function SidebarHeader({
  collapsed,
  onCreateWorkspace,
}: {
  collapsed: boolean;
  // Demo-only: when passed, appends a "Create workspace" row that pitches the
  // subscription upsell instead of leaving it disabled ("not available yet").
  onCreateWorkspace?: () => void;
}) {
  const isDemo = window.location.pathname.startsWith('/demo');
  const {
    data,
    setFilter,
    loadNextRef,
    filter,
    loading: activeWorkspaceLoading,
    hasNextPage,
    initialLoading,
    handleClearFilter,
    currentWorkspaceCount,
  } = useActiveWorkspaces();
  const { handleWorkspaceSwitch, authMeWorkspace, dollarAmount, loading } = useWorkSpaceSwitch();
  const { isWalletAmountHidden, toggleWalletAmountVisibility } = useWalletVisibilityStore();
  const navigate = useNavigate();
  const { label: workspaceLabel } = authMeWorkspace?.workspaceAuthMeV2?.data ?? {};
  // Demo: the workspace query resolves async through the mock link, so the
  // very first paint can render before it lands — always show a name instead
  // of a blank space beside the avatar.
  const label = isDemo ? workspaceLabel || 'Demo Workspace' : workspaceLabel;
  const profilePicture = authMeWorkspace?.workspaceAuthMeV2?.data?.logo?.url;

  const { t } = useTranslation();
  const loadMoreSkeleton =
    initialLoading || hasNextPage ? (
      <div ref={loadNextRef}>
        <Skeleton active paragraph={false} />
      </div>
    ) : undefined;

  const activeWorkspaces = data?.data;
  const items: MenuProps['items'] = useMemo(() => {
    const workspaceItems: MenuProps['items'] =
      activeWorkspaces?.map((workspace) => {
        const isCurrentWorkspace = workspace?.isCurrent;
        return {
          key: String(workspace?.id),
          onClick: () => {
            // Demo: the workspace card is presentational only, switching
            // workspaces is a real cookie/session mutation the demo can't do.
            if (isDemo) return;
            if (isCurrentWorkspace) return;
            handleWorkspaceSwitch(workspace?.id);
          },
          label: <WorkspaceItem workspace={workspace || {}} />,
          className: 'px-1.5! h-9! my-0.5!',
        };
      }) || [];

    if (!activeWorkspaces?.length && !activeWorkspaceLoading) {
      return [
        {
          key: 'empty',
          disabled: true,
          label: <Empty description={capitalize(t('no_workspaces_found'))} />,
          style: { height: 'auto' },
        },
      ];
    }

    return workspaceItems;
  }, [activeWorkspaces, filter.query, setFilter, loadNextRef, hasNextPage, initialLoading, activeWorkspaceLoading]);

  if (loading)
    return (
      <div className="h-16" style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
        <Skeleton
          active
          avatar={{ shape: 'circle', size: 'large' }}
          title={{ width: 200 }}
          paragraph={{ rows: 1, width: 80, style: { marginTop: '16px' } }}
        />
      </div>
    );
  return (
    <div className="px-4 h-14 overflow-hidden border border-x-0 border-gray-500 flex items-center justify-between">
      <section className={clsx('flex items-center gap-2.5 relative transition-all duration-500', collapsed && 'pl-1')}>
        <Popover
          trigger={['click']}
          classNames={{
            body: 'p-0! rounded-2xl!',
          }}
          onOpenChange={(open) => {
            if (!open) handleClearFilter();
          }}
          content={
            <div className="bg-white border border-gray-500 rounded-md overflow-hidden w-[229px]! max-h-[2390px]! flex flex-col">
              <SearchWorkspace setFilter={setFilter} filter={filter} />
              <div className="overflow-autoflex-1">
                <Menu
                  mode="inline"
                  inlineCollapsed={false}
                  items={[
                    ...items,
                    {
                      key: 'lazy-loader',
                      style: { display: initialLoading || hasNextPage ? 'block' : 'none' },
                      label: loadMoreSkeleton,
                    },
                  ]}
                  rootClassName="border-none!"
                />
              </div>
              {onCreateWorkspace && <CreateWorkspace onClick={onCreateWorkspace} />}
            </div>
          }
        >
          <div className="border border-gray-500 rounded-full relative cursor-pointer">
            <Badge count={currentWorkspaceCount}>
              <AppAvatar size={32} src={profilePicture} name={label} className="[&_span]:text-[14px]!" />
            </Badge>
            <RiArrowDownSLine className="absolute left-5 top-5.5 bg-gray-400 size-3 rounded-xl" />
          </div>
        </Popover>
        <span className={clsx(collapsed && 'pl-4 opacity-0', 'min-w-[200px] transition-all duration-500 leading-px')}>
          <p className="text-base font-medium leading-5 text-text-900">{label}</p>
          <span className="inline-flex items-center gap-1">
            <small
              className="text-xs font-medium cursor-pointer text-text-700 tracking-wider"
              onClick={() => navigate('/settings/wallet?tab=Overview')}
            >
              {dollarAmount ? (isWalletAmountHidden ? 'XX.XX' : getFormattedWalletAmount(dollarAmount)) : null}
            </small>
            {dollarAmount ? (
              isWalletAmountHidden ? (
                <RiEyeOffLine className="text-primary size-3.5 cursor-pointer" onClick={toggleWalletAmountVisibility} />
              ) : (
                <RiEyeLine className="text-primary size-3.5 cursor-pointer" onClick={toggleWalletAmountVisibility} />
              )
            ) : null}
          </span>
        </span>
      </section>
    </div>
  );
}
